import React, { useState, useEffect } from "react";
import {
  BarChart3, Users, CreditCard, FileText, Mail, Clock, TrendingUp,
  LogOut, ChevronRight, Eye, Trash2, CheckCircle, AlertCircle,
  Shield, Package, ArrowRight, X, Settings, Download
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://enrapi.tikabasaapp.com"}/api`;

// Admin Login
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("admin_token", res.data.token);
      onLogin(res.data.token);
    } catch {
      setError("Geçersiz kullanıcı adı veya şifre");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="bg-white border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-navy rounded flex items-center justify-center">
              <Shield className="w-5 h-5 text-gold" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-navy">E&R Admin</h1>
              <p className="text-xs text-gray-500">Yönetim Paneli</p>
            </div>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-4 flex items-center gap-2" data-testid="login-error">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1.5">Kullanıcı Adı</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 text-sm"
                placeholder="admin"
                required
                data-testid="admin-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1.5">Şifre</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 text-sm"
                placeholder="********"
                required
                data-testid="admin-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-navy text-white py-2.5 text-sm font-medium hover:bg-navy-light transition-colors disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Sidebar
const Sidebar = ({ active, onNavigate, onLogout, unreadCount }) => {
  const items = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "purchases", label: "Satışlar", icon: CreditCard },
    { id: "analyses", label: "İhtiyaç Analizleri", icon: FileText },
    { id: "messages", label: "Mesajlar", icon: Mail, badge: unreadCount },
    { id: "catalog", label: "Katalog Talepleri", icon: Download },
    { id: "tests", label: "Testler", icon: Package },
  ];

  return (
    <aside className="w-64 bg-navy min-h-screen flex-shrink-0 hidden lg:block" data-testid="admin-sidebar">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gold/20 rounded flex items-center justify-center">
            <Settings className="w-4 h-4 text-gold" />
          </div>
          <span className="text-white font-semibold text-sm">E&R Admin</span>
        </div>
      </div>
      <nav className="p-4 space-y-1">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => onNavigate(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm rounded transition-colors ${
              active === item.id ? "bg-white/10 text-gold" : "text-gray-300 hover:bg-white/5 hover:text-white"
            }`}
            data-testid={`sidebar-${item.id}`}
          >
            <item.icon className="w-4 h-4" />
            <span className="flex-1 text-left">{item.label}</span>
            {item.badge > 0 && (
              <span className="bg-gold text-navy text-xs font-bold px-2 py-0.5 rounded-full">{item.badge}</span>
            )}
          </button>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 w-64 p-4 border-t border-white/10">
        <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-400 hover:text-white text-sm transition-colors" data-testid="admin-logout">
          <LogOut className="w-4 h-4" />
          Çıkış Yap
        </button>
      </div>
    </aside>
  );
};

// Mobile Header
const MobileHeader = ({ active, onNavigate, onLogout, unreadCount }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const items = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "purchases", label: "Satışlar", icon: CreditCard },
    { id: "analyses", label: "Analizler", icon: FileText },
    { id: "messages", label: "Mesajlar", icon: Mail, badge: unreadCount },
    { id: "catalog", label: "Katalog Talepleri", icon: Download },
    { id: "tests", label: "Testler", icon: Package },
  ];

  return (
    <div className="lg:hidden bg-navy px-4 py-3 flex items-center justify-between sticky top-0 z-50">
      <span className="text-white font-semibold text-sm">E&R Admin</span>
      <div className="flex items-center gap-2">
        <button onClick={() => setMenuOpen(!menuOpen)} className="text-white p-2" data-testid="admin-mobile-menu">
          {menuOpen ? <X className="w-5 h-5" /> : <BarChart3 className="w-5 h-5" />}
        </button>
      </div>
      {menuOpen && (
        <div className="absolute top-full left-0 right-0 bg-navy-dark py-2 z-50 shadow-xl">
          {items.map((item) => (
            <button key={item.id} onClick={() => { onNavigate(item.id); setMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm ${active === item.id ? "text-gold" : "text-gray-300"}`}>
              <item.icon className="w-4 h-4" />
              {item.label}
              {item.badge > 0 && <span className="bg-gold text-navy text-xs font-bold px-2 py-0.5 rounded-full ml-auto">{item.badge}</span>}
            </button>
          ))}
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-6 py-3 text-sm text-gray-400 border-t border-white/10 mt-2">
            <LogOut className="w-4 h-4" /> Çıkış
          </button>
        </div>
      )}
    </div>
  );
};

// Stat Card
const StatCard = ({ icon: Icon, label, value, sub, color = "bg-navy" }) => (
  <div className="bg-white border border-gray-100 p-5" data-testid={`stat-${label}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-navy mt-1">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 ${color} rounded flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </div>
);

// Format date
const fmtDate = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  return dt.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Dashboard View
const DashboardView = ({ data }) => {
  if (!data) return <p className="text-gray-500">Yükleniyor...</p>;
  const s = data.stats;

  return (
    <div data-testid="admin-dashboard">
      <h2 className="text-xl font-bold text-navy mb-6">Dashboard</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={CreditCard} label="Satışlar" value={s.success_purchases} sub={`${s.purchases} toplam`} color="bg-green-600" />
        <StatCard icon={TrendingUp} label="Gelir" value={`₺${s.total_revenue.toLocaleString("tr-TR")}`} color="bg-navy" />
        <StatCard icon={FileText} label="Analizler" value={s.analyses} color="bg-blue-600" />
        <StatCard icon={Mail} label="Mesajlar" value={s.messages} sub={`${s.unread_messages} okunmamış`} color="bg-gold" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-navy mb-4">Son Satışlar</h3>
          {data.recent_purchases.length === 0 ? <p className="text-sm text-gray-400">Henüz satış yok</p> : (
            <div className="space-y-3">
              {data.recent_purchases.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                  <div>
                    <p className="font-medium text-navy">{p.buyer_name || p.buyer_email}</p>
                    <p className="text-xs text-gray-400">{fmtDate(p.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-navy">₺{p.price}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "success" ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`}>
                      {p.status === "success" ? "Başarılı" : "Beklemede"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-100 p-5">
          <h3 className="text-sm font-semibold text-navy mb-4">Son İhtiyaç Analizleri</h3>
          {data.recent_analyses.length === 0 ? <p className="text-sm text-gray-400">Henüz analiz yok</p> : (
            <div className="space-y-3">
              {data.recent_analyses.map((a, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                  <div>
                    <p className="font-medium text-navy">{a.company_info?.company_name || "-"}</p>
                    <p className="text-xs text-gray-400">{a.company_info?.sector} / {a.company_info?.employee_count}</p>
                  </div>
                  <p className="text-xs text-gray-400">{fmtDate(a.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Purchases View
const PurchasesView = ({ token }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/purchases`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="text-gray-500">Yükleniyor...</p>;

  const renderInvoice = (inv) => {
    if (!inv) return <span className="text-gray-400 italic">Fatura bilgisi yok</span>;
    const ctype = inv.customer_type === "corporate" ? "Kurumsal" : "Bireysel";
    return (
      <div className="text-xs space-y-1">
        <div><span className="font-medium text-navy">Tip:</span> {ctype}</div>
        {inv.customer_type === "individual" ? (
          <div><span className="font-medium text-navy">TC No:</span> {inv.tc_no || "-"}</div>
        ) : (
          <>
            <div><span className="font-medium text-navy">Ünvan:</span> {inv.company_name || "-"}</div>
            <div><span className="font-medium text-navy">Vergi No:</span> {inv.tax_number || "-"}</div>
            <div><span className="font-medium text-navy">Vergi Dairesi:</span> {inv.tax_office || "-"}</div>
          </>
        )}
        {(inv.address || inv.city) && (
          <div><span className="font-medium text-navy">Adres:</span> {[inv.address, inv.city, inv.zip_code].filter(Boolean).join(", ")}</div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="admin-purchases">
      <h2 className="text-xl font-bold text-navy mb-6">Satışlar ({data.length})</h2>
      {data.length === 0 ? <p className="text-gray-400">Henüz satış bulunmuyor.</p> : (
        <div className="bg-white border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 font-medium text-gray-600 w-8"></th>
                <th className="px-4 py-3 font-medium text-gray-600">Müşteri</th>
                <th className="px-4 py-3 font-medium text-gray-600">E-posta</th>
                <th className="px-4 py-3 font-medium text-gray-600">Tip</th>
                <th className="px-4 py-3 font-medium text-gray-600">Tutar</th>
                <th className="px-4 py-3 font-medium text-gray-600">Erişim Kodu</th>
                <th className="px-4 py-3 font-medium text-gray-600">Durum</th>
                <th className="px-4 py-3 font-medium text-gray-600">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {data.map((p, i) => {
                const isOpen = expandedId === (p.id || i);
                const ctype = p.invoice?.customer_type;
                return (
                  <React.Fragment key={p.id || i}>
                    <tr
                      className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedId(isOpen ? null : (p.id || i))}
                      data-testid={`purchase-row-${i}`}
                    >
                      <td className="px-4 py-3 text-gray-400">{isOpen ? "▾" : "▸"}</td>
                      <td className="px-4 py-3 font-medium text-navy">{p.buyer_name}</td>
                      <td className="px-4 py-3 text-gray-600">{p.buyer_email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ctype === "corporate" ? "bg-blue-50 text-blue-700" : ctype === "individual" ? "bg-purple-50 text-purple-700" : "bg-gray-100 text-gray-500"}`}>
                          {ctype === "corporate" ? "Kurumsal" : ctype === "individual" ? "Bireysel" : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold">₺{p.price}</td>
                      <td className="px-4 py-3"><code className="bg-gray-50 px-2 py-1 text-xs">{p.access_code || "-"}</code></td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${p.status === "success" ? "bg-green-50 text-green-700" : p.status === "failed" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-700"}`}>
                          {p.status === "success" ? "Başarılı" : p.status === "failed" ? "Başarısız" : "Beklemede"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{fmtDate(p.created_at)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50/50">
                        <td></td>
                        <td colSpan={7} className="px-4 py-4">
                          <div className="text-xs text-gray-600 mb-2 font-medium uppercase tracking-wide">Fatura Bilgileri</div>
                          {renderInvoice(p.invoice)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Analyses View
const AnalysesView = ({ token }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/analyses`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="text-gray-500">Yükleniyor...</p>;

  return (
    <div data-testid="admin-analyses">
      <h2 className="text-xl font-bold text-navy mb-6">İhtiyaç Analizleri ({data.length})</h2>
      {data.length === 0 ? <p className="text-gray-400">Henüz analiz bulunmuyor.</p> : (
        <div className="space-y-3">
          {data.map((a, i) => (
            <div key={i} className="bg-white border border-gray-100">
              <button onClick={() => setExpanded(expanded === i ? null : i)} className="w-full px-5 py-4 flex items-center justify-between text-left">
                <div>
                  <p className="font-semibold text-navy">{a.company_info?.company_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{a.company_info?.contact_name} - {a.company_info?.contact_email}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">{fmtDate(a.created_at)}</p>
                  <p className="text-xs text-gray-500">{a.company_info?.sector}</p>
                </div>
              </button>
              {expanded === i && (
                <div className="px-5 pb-5 border-t border-gray-50 pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div><p className="text-xs text-gray-400">Şirket</p><p className="text-sm font-medium">{a.company_info?.company_name}</p></div>
                    <div><p className="text-xs text-gray-400">Sektör</p><p className="text-sm">{a.company_info?.sector}</p></div>
                    <div><p className="text-xs text-gray-400">Çalışan</p><p className="text-sm">{a.company_info?.employee_count}</p></div>
                    <div><p className="text-xs text-gray-400">Telefon</p><p className="text-sm">{a.company_info?.contact_phone}</p></div>
                  </div>
                  {a.scores && (
                    <div>
                      <p className="text-xs font-semibold text-navy mb-2">Skorlar</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(a.scores).map(([k, v]) => (
                          <div key={k} className="bg-gray-50 px-3 py-2">
                            <p className="text-xs text-gray-500">{k}</p>
                            <p className={`text-sm font-bold ${v >= 70 ? "text-green-600" : v >= 40 ? "text-yellow-600" : "text-red-600"}`}>{Math.round(v)}%</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Messages View
const MessagesView = ({ token, onUpdate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = () => {
    axios.get(`${API}/admin/messages`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchMessages(); }, [token]);

  const markRead = async (id) => {
    await axios.put(`${API}/admin/messages/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    fetchMessages();
    onUpdate();
  };

  const deleteMsg = async (id) => {
    if (!window.confirm("Bu mesajı silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/admin/messages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    fetchMessages();
    onUpdate();
  };

  if (loading) return <p className="text-gray-500">Yükleniyor...</p>;

  return (
    <div data-testid="admin-messages">
      <h2 className="text-xl font-bold text-navy mb-6">Mesajlar ({data.length})</h2>
      {data.length === 0 ? <p className="text-gray-400">Henüz mesaj bulunmuyor.</p> : (
        <div className="space-y-3">
          {data.map((m, i) => (
            <div key={i} className={`bg-white border ${m.is_read ? "border-gray-100" : "border-gold/30 bg-gold/5"} p-5`} data-testid={`message-${i}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-navy flex items-center gap-2">
                    {m.name}
                    {!m.is_read && <span className="w-2 h-2 bg-gold rounded-full" />}
                  </p>
                  <p className="text-xs text-gray-500">{m.email} {m.phone && `/ ${m.phone}`}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{fmtDate(m.created_at)}</span>
                  {!m.is_read && (
                    <button onClick={() => markRead(m.id)} className="text-green-600 hover:text-green-800 p-1" title="Okundu İşaretle">
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => deleteMsg(m.id)} className="text-red-400 hover:text-red-600 p-1" title="Sil">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Tests View
const TestsView = ({ token }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // { [testId]: { price, discount_percent } }
  const [saving, setSaving] = useState(null);
  const [savedId, setSavedId] = useState(null);

  const fetchTests = () => {
    axios.get(`${API}/admin/tests`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchTests(); }, [token]);

  const toggleActive = async (test) => {
    await axios.put(`${API}/admin/tests/${test.id}`, { is_active: !test.is_active }, { headers: { Authorization: `Bearer ${token}` } });
    fetchTests();
  };

  const getEdit = (test, field) => {
    if (edits[test.id] && edits[test.id][field] !== undefined) return edits[test.id][field];
    return field === "discount_percent" ? (test.discount_percent ?? 0) : test.price;
  };

  const setEdit = (testId, field, value) => {
    setEdits(prev => ({ ...prev, [testId]: { ...(prev[testId] || {}), [field]: value } }));
    setSavedId(null);
  };

  const savePricing = async (test) => {
    const price = parseFloat(getEdit(test, "price"));
    const discount = parseFloat(getEdit(test, "discount_percent"));
    if (isNaN(price) || price < 0) return alert("Geçersiz fiyat");
    if (isNaN(discount) || discount < 0 || discount > 100) return alert("İndirim 0-100 arası olmalı");

    setSaving(test.id);
    try {
      await axios.put(`${API}/admin/tests/${test.id}`,
        { price, discount_percent: discount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedId(test.id);
      setEdits(prev => { const next = { ...prev }; delete next[test.id]; return next; });
      await fetchTests();
      setTimeout(() => setSavedId(null), 2200);
    } catch (e) {
      alert(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(null);
    }
  };

  const isDirty = (test) => {
    const e = edits[test.id];
    if (!e) return false;
    const priceChanged = e.price !== undefined && parseFloat(e.price) !== test.price;
    const discountChanged = e.discount_percent !== undefined && parseFloat(e.discount_percent) !== (test.discount_percent ?? 0);
    return priceChanged || discountChanged;
  };

  if (loading) return <p className="text-gray-500">Yükleniyor...</p>;

  return (
    <div data-testid="admin-tests">
      <h2 className="text-xl font-bold text-navy mb-2">Testler & Fiyatlar ({data.length})</h2>
      <p className="text-sm text-gray-500 mb-6">Her test için fiyat ve indirim oranını düzenleyip kaydedebilirsiniz. İndirim oranı %0-%100 aralığındadır.</p>
      {data.length === 0 ? <p className="text-gray-400">Henüz test bulunmuyor.</p> : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.map((t, i) => {
            const editedPrice = parseFloat(getEdit(t, "price")) || 0;
            const editedDiscount = parseFloat(getEdit(t, "discount_percent")) || 0;
            const previewFinal = Math.max(0, editedPrice - (editedPrice * editedDiscount / 100));
            const dirty = isDirty(t);
            return (
              <div key={t.id} className={`bg-white border border-gray-100 p-5 ${!t.is_active ? "opacity-60" : ""}`} data-testid={`test-item-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-navy">{t.name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">{t.category}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${t.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {t.is_active ? "Aktif" : "Pasif"}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{t.description}</p>
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                  <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{t.duration_minutes} dk</span>
                  <span>{t.question_count} soru</span>
                </div>

                {/* Pricing editor */}
                <div className="border-t border-gray-100 pt-4 mb-4">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Fiyat (₺)</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={getEdit(t, "price")}
                        onChange={(e) => setEdit(t.id, "price", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gold"
                        data-testid={`test-price-input-${i}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">İndirim (%)</label>
                      <input
                        type="number" step="1" min="0" max="100"
                        value={getEdit(t, "discount_percent")}
                        onChange={(e) => setEdit(t.id, "discount_percent", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-gold"
                        data-testid={`test-discount-input-${i}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-100 px-3 py-2 text-sm mb-3">
                    <span className="text-gray-600">Müşteri ödeyecek:</span>
                    <span className="font-bold text-navy" data-testid={`test-final-price-${i}`}>
                      {editedDiscount > 0 && (
                        <span className="text-gray-400 line-through text-xs mr-2">₺{editedPrice.toFixed(2)}</span>
                      )}
                      ₺{previewFinal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => savePricing(t)}
                      disabled={!dirty || saving === t.id}
                      className={`flex-1 text-xs font-medium px-4 py-2 transition-colors ${dirty ? "bg-navy text-white hover:bg-navy-light" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                      data-testid={`test-save-pricing-${i}`}
                    >
                      {saving === t.id ? "Kaydediliyor..." : savedId === t.id ? "✓ Kaydedildi" : "Fiyatı Güncelle"}
                    </button>
                    <button
                      onClick={() => toggleActive(t)}
                      className={`text-xs px-3 py-2 border transition-colors ${t.is_active ? "border-red-200 text-red-600 hover:bg-red-50" : "border-green-200 text-green-600 hover:bg-green-50"}`}
                      data-testid={`test-toggle-${i}`}
                    >
                      {t.is_active ? "Pasif Yap" : "Aktif Yap"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Catalog Requests View — kullanıcıların indirdiği kataloglara ait email talepleri
const CatalogRequestsView = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchRequests = () => {
    setLoading(true);
    axios.get(`${API}/admin/catalog-requests`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRequests(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRequests(); }, [token]);

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    await axios.delete(`${API}/admin/catalog-requests/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    fetchRequests();
  };

  const exportCSV = () => {
    if (requests.length === 0) return;
    const header = ["Tarih", "E-posta", "Ad Soyad", "Şirket", "Telefon", "IP"];
    const rows = requests.map(r => [
      new Date(r.created_at).toLocaleString("tr-TR"),
      r.email || "",
      r.full_name || "",
      r.company_name || "",
      r.phone || "",
      r.ip || "",
    ]);
    const csv = [header, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `katalog-talepleri-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = requests.filter(r => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.email || "").toLowerCase().includes(q) ||
      (r.full_name || "").toLowerCase().includes(q) ||
      (r.company_name || "").toLowerCase().includes(q)
    );
  });

  if (loading) return <div className="p-8 text-center text-gray-500">Yükleniyor...</div>;

  return (
    <div data-testid="admin-catalog-requests">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-navy">Katalog Talepleri</h2>
          <p className="text-sm text-gray-500 mt-1">
            Eğitim kataloğunu indirmek için e-posta bırakan kullanıcılar.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="E-posta, isim veya şirket ara..."
            className="px-3 py-2 border border-gray-200 focus:border-gold focus:outline-none text-sm w-full sm:w-64"
            data-testid="catalog-search"
          />
          <button
            onClick={exportCSV}
            disabled={requests.length === 0}
            className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
            data-testid="catalog-export-csv"
          >
            <Download className="w-4 h-4 text-gold" /> CSV
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <span className="text-sm font-semibold text-navy">
            {filtered.length} kayıt {search && `(${requests.length} toplam)`}
          </span>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Download className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Henüz katalog talebi yok.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-navy text-xs uppercase tracking-wider">Tarih</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy text-xs uppercase tracking-wider">E-posta</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy text-xs uppercase tracking-wider">Ad Soyad</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy text-xs uppercase tracking-wider">Şirket</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy text-xs uppercase tracking-wider">Telefon</th>
                  <th className="text-right px-5 py-3 font-semibold text-navy text-xs uppercase tracking-wider">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`catalog-row-${r.id}`}>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-3 text-navy font-medium">
                      <a href={`mailto:${r.email}`} className="hover:text-gold">{r.email}</a>
                    </td>
                    <td className="px-5 py-3 text-gray-700">{r.full_name || <span className="text-gray-300">—</span>}</td>
                    <td className="px-5 py-3 text-gray-700">{r.company_name || <span className="text-gray-300">—</span>}</td>
                    <td className="px-5 py-3 text-gray-700">
                      {r.phone ? <a href={`tel:${r.phone}`} className="hover:text-gold">{r.phone}</a> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Sil"
                        data-testid={`catalog-delete-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Admin Panel
const AdminPanel = ({ onBack }) => {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [page, setPage] = useState("dashboard");
  const [dashData, setDashData] = useState(null);
  const [unread, setUnread] = useState(0);

  const fetchDashboard = () => {
    if (!token) return;
    axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setDashData(r.data); setUnread(r.data.stats.unread_messages); })
      .catch(err => { if (err.response?.status === 401) handleLogout(); });
  };

  useEffect(() => { fetchDashboard(); }, [token]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) return <AdminLogin onLogin={(t) => setToken(t)} />;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row" data-testid="admin-panel">
      <Sidebar active={page} onNavigate={setPage} onLogout={handleLogout} unreadCount={unread} />
      <MobileHeader active={page} onNavigate={setPage} onLogout={handleLogout} unreadCount={unread} />
      <main className="flex-1 p-4 lg:p-8 min-h-screen">
        <div className="mb-6 flex items-center justify-between">
          <button onClick={onBack} className="text-sm text-gray-500 hover:text-navy flex items-center gap-1" data-testid="back-to-site">
            <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Siteye Dön
          </button>
        </div>
        {page === "dashboard" && <DashboardView data={dashData} />}
        {page === "purchases" && <PurchasesView token={token} />}
        {page === "analyses" && <AnalysesView token={token} />}
        {page === "messages" && <MessagesView token={token} onUpdate={fetchDashboard} />}
        {page === "catalog" && <CatalogRequestsView token={token} />}
        {page === "tests" && <TestsView token={token} />}
      </main>
    </div>
  );
};

export default AdminPanel;
